import React from "react";
import { MoreVertical, Edit, Palette, Archive, ArrowRight, Check, Pipette } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";

interface ColumnMenuProps {
  columnId: string;
  currentTitle: string;
  currentBgColor?: string;
  isLastColumn?: boolean;
  lastColumnTitle?: string;
  isProtectedColumn?: boolean;
  onUpdateTitle: (columnId: string, title: string) => void;
  onUpdateBgColor: (columnId: string, color: string) => void;
  onArchiveAll?: () => void;
  onDeleteColumn?: () => void;
  onMoveToLast?: () => void;
  hasTasksToArchive?: boolean;
  hasTasksToMove?: boolean;
  onRename?: () => void;
}

const colorPresets = [
  { name: "Default", value: "", bgClass: "bg-muted/70", hex: "#f1f5f9", borderClass: "border-border" },
  { name: "Blue", value: "bg-board-column-blue", bgClass: "bg-blue-100 dark:bg-blue-950/70", hex: "#dbeafe", borderClass: "border-blue-300 dark:border-blue-800" },
  { name: "Green", value: "bg-board-column-green", bgClass: "bg-emerald-100 dark:bg-emerald-950/70", hex: "#dcfce7", borderClass: "border-emerald-300 dark:border-emerald-800" },
  { name: "Red", value: "bg-board-column-red", bgClass: "bg-rose-100 dark:bg-rose-950/70", hex: "#fee2e2", borderClass: "border-rose-300 dark:border-rose-800" },
  { name: "Accent", value: "bg-board-column-primary", bgClass: "bg-purple-100 dark:bg-purple-950/70", hex: "#f3e8ff", borderClass: "border-purple-300 dark:border-purple-800" },
  { name: "Yellow", value: "bg-board-column-yellow", bgClass: "bg-amber-100 dark:bg-amber-950/70", hex: "#fef9c3", borderClass: "border-amber-300 dark:border-amber-800" },
  { name: "Orange", value: "bg-board-column-orange", bgClass: "bg-orange-100 dark:bg-orange-950/70", hex: "#ffedd5", borderClass: "border-orange-300 dark:border-orange-800" },
];

export const ColumnMenu: React.FC<ColumnMenuProps> = ({
  columnId,
  currentTitle,
  currentBgColor = "",
  isLastColumn = false,
  lastColumnTitle = "Last Column",
  isProtectedColumn = false,
  onUpdateTitle,
  onUpdateBgColor,
  onArchiveAll,
  onMoveToLast,
  hasTasksToArchive = false,
  hasTasksToMove = false,
  onRename,
}) => {
  const handleColorSelect = (color: string) => {
    onUpdateBgColor(columnId, color);
  };

  const isCustomColorActive = Boolean(
    currentBgColor && !colorPresets.some((p) => p.value === currentBgColor)
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="h-6 w-6">
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={onRename}>
          <Edit className="h-4 w-4" />
          Rename
        </DropdownMenuItem>

        {/* Color Submenu with circular single-row inline palette and custom color option */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="flex items-center gap-2 cursor-pointer">
            <Palette className="h-4 w-4" />
            <span>Color</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuSubContent className="p-2 bg-card border-border shadow-lg rounded-xl">
            <div className="space-y-1.5">
              <div className="text-[10px] font-semibold text-primary uppercase tracking-wider px-0.5">
                Column Color
              </div>
              <div className="flex items-center gap-1.5 py-0.5">
                {colorPresets.map((preset) => {
                  const isSelected = (!currentBgColor && !preset.value) || currentBgColor === preset.value;
                  return (
                    <button
                      key={preset.name}
                      type="button"
                      onClick={() => handleColorSelect(preset.value)}
                      className={`relative w-5 h-5 rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${preset.bgClass} ${preset.borderClass} ${
                        isSelected
                          ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-105 border-primary"
                          : "hover:ring-1 hover:ring-primary/40"
                      }`}
                      title={preset.name}
                    >
                      {isSelected && (
                        <Check className="h-2.5 w-2.5 text-foreground drop-shadow-xs" strokeWidth={3} />
                      )}
                    </button>
                  );
                })}

                {/* Custom Color Option */}
                <label
                  className={`relative w-5 h-5 rounded-full border transition-all hover:scale-115 flex items-center justify-center shrink-0 cursor-pointer shadow-xs ${
                    isCustomColorActive
                      ? "ring-2 ring-primary ring-offset-1 ring-offset-background scale-105 border-primary"
                      : "border-border/60 hover:ring-1 hover:ring-primary/40"
                  }`}
                  style={
                    isCustomColorActive
                      ? { backgroundColor: currentBgColor }
                      : {
                          background:
                            "conic-gradient(from 180deg at 50% 50%, #f43f5e 0deg, #fbbf24 60deg, #34d399 120deg, #38bdf8 180deg, #818cf8 240deg, #c084fc 300deg, #f43f5e 360deg)",
                        }
                  }
                  title="Custom color"
                >
                  <input
                    type="color"
                    value={currentBgColor?.startsWith("#") ? currentBgColor : "#3b82f6"}
                    onChange={(e) => handleColorSelect(e.target.value)}
                    className="sr-only"
                  />
                  {isCustomColorActive ? (
                    <Check className="h-2.5 w-2.5 text-white drop-shadow-md" strokeWidth={3} />
                  ) : (
                    <div className="w-3 h-3 rounded-full bg-background flex items-center justify-center shadow-xs">
                      <Pipette className="h-1.5 w-1.5 text-foreground" />
                    </div>
                  )}
                </label>
              </div>
            </div>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        {!isLastColumn && onMoveToLast && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={onMoveToLast}
              disabled={!hasTasksToMove}
            >
              <ArrowRight className="h-4 w-4" />
              Move to {lastColumnTitle}
            </DropdownMenuItem>
          </>
        )}
        {isLastColumn && onArchiveAll && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={onArchiveAll}
              disabled={!hasTasksToArchive}
            >
              <Archive className="h-4 w-4" />
              Move to Archive
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};